import { AppServiceDetail } from '@bumble/app/__generated__/data-contracts';
import { Indicator } from '@bumble/app/components/Indicator';
import NoData from '@bumble/app/components/NoData';
import { TabGrid, TabGridStructure } from '@bumble/app/components/TabGrid';
import { theme } from '@bumble/app/theme';
import getDate from '@bumble/app/utils/get-date';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';

const accordionStyles = {
  boxShadow: 'none',
  '&::before': {
    display: 'none',
  },
};

const accordionSummaryStyles = {
  flexDirection: 'row-reverse',
  fontWeight: 700,
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(-90deg)',
    color: theme.palette.primary.main,
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(0deg)'
  },
}

const getLabel = (data: AppServiceDetail | undefined) => {
  const deployment = data?.deployment;
  if (data !== undefined && data?.deployment) {
    switch (deployment) {
      case 'aws':
        return 'AWS Endpoint Service Name';
      case 'azure':
        return 'Private Link Service ID';
      default:
        return 'App Onprem DNS Name or IP';
    }
  } else {
    return 'N/A';
  }
};

export const AppServicesTabDetails = ({ data }: { data: AppServiceDetail | undefined }) => {
  if (data === undefined) return <NoData />;

  const tabDataGeneral: TabGridStructure[] = [
    { label: 'App Service Name', content: data?.app_service_name || 'N/A' },
    { label: getLabel(data), content: data?.fqdn || 'N/A' },
    { label: 'Account Id', content: data?.account_id || 'N/A' },
    { label: 'App Service Id', content: data?.app_service_id || 'N/A' },
    { label: 'Description', content: data?.description || 'N/A' },
    { label: 'Created at', content: data !== undefined && data?.created_at ? getDate(data?.created_at) : 'N/A' },
    { label: 'Admin State', content: data?.admin_state || 'N/A' },
    { label: 'OP State', content: data?.op_state !== undefined ? <Indicator active={data?.op_state === 'up'} /> : 'N/A' },
    { label: 'Admin State Change at', content: data !== undefined && data?.admin_state_change_at ? getDate(data?.admin_state_change_at) : 'N/A' },
    { label: 'OP State Change at', content: data !== undefined && data?.op_state_change_at ? getDate(data?.op_state_change_at) : 'N/A' },
  ];

  const tabDataAws: TabGridStructure[] = [
    { label: 'Deployment', content: data?.deployment || 'N/A' },
    { label: 'App Protocol', content: data?.proto || 'N/A' },
    { label: 'Port', content: data?.port?.toString() || 'N/A' },
    { label: 'Region', content: data?.region || 'N/A' },
  ];

  const tabDataAzure: TabGridStructure[] = [
    { label: 'Deployment', content: data?.deployment || 'N/A' },
    { label: 'App Protocol', content: data?.proto || 'N/A' },
    { label: 'Port', content: data?.port?.toString() || 'N/A' },
    { label: 'Region', content: data?.region || 'N/A' },
    { label: 'Customer User Email', content: data?.azure_user_email || 'N/A' },
    { label: 'Bumblebee VNet Resource ID', content: data?.local_vnet_resource_id || 'N/A' },
    { label: 'Customer VNet Resource ID', content: data?.remote_vnet_resource_id || 'N/A' },
  ];

  const tabDataOnprem: TabGridStructure[] = [
    { label: 'Deployment', content: data?.deployment || 'N/A' },
    { label: getLabel(data), content: data?.network_mode_enabled !== undefined && (data?.network_mode_enabled == false) ? data?.fqdn || 'N/A' : 'N/A' },
    { label: 'Network Mode Enabled', content: data?.network_mode_enabled !== undefined && (data?.network_mode_enabled == true) ? 'yes' : 'no' },
    { label: 'BGP Enabled', content: data?.bgp_enabled !== undefined && (data?.bgp_enabled == true) ? 'yes' : 'no' },
    { label: 'Address Translation Enabled', content: data?.address_translation_enabled !== undefined && (data?.address_translation_enabled == true) ? 'yes' : 'no' },
    { label: 'BGP Local ASN', content: data?.bgp_local_asn?.toString() || 'N/A' },
    { label: 'BGP Peer ASN', content: data?.bgp_peer_asn?.toString() || 'N/A' },
    { label: 'BGP Local IP', content: data?.bgp_local_ip || 'N/A' },
    { label: 'BGP Peer IP', content: data?.bgp_peer_ip || 'N/A' },
    { label: 'Virtual Subnet Pools', content: data?.virtual_subnet_pools || 'N/A' },
    { label: 'Real Subnets', content: data?.real_subnets || 'N/A' },
    { label: 'Virtual Subnets', content: data?.virtual_subnets || 'N/A' },
  ];

  const tabDataContacts: TabGridStructure[] = [
    { label: 'Primary Contact', content: data?.primary_contact || 'N/A' },
    { label: 'Secondary Contact', content: data?.secondary_contact || 'N/A' },
  ];

  const getSpecificTabData = () => {
    switch (data?.deployment) {
      case 'onprem':
        return <TabGrid data={tabDataOnprem} />;
      case 'azure':
        return <TabGrid data={tabDataAzure} />;
      default:
        return <TabGrid data={tabDataAws} />;
    }
  };
  return (
    <Box>
      <Accordion sx={accordionStyles}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={accordionSummaryStyles}
        >
          General
        </AccordionSummary>
        <AccordionDetails>
          <TabGrid data={tabDataGeneral} />
        </AccordionDetails>
      </Accordion>
      <Accordion sx={accordionStyles}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={accordionSummaryStyles}
        >
          Specific
        </AccordionSummary>
        <AccordionDetails>
          {getSpecificTabData()}
        </AccordionDetails>
      </Accordion>
      <Accordion sx={accordionStyles}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={accordionSummaryStyles}
        >
          Contacts
        </AccordionSummary>
        <AccordionDetails>
          <TabGrid data={tabDataContacts} colWidth={6} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
